import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Box,
  History,
  Truck,
  Calendar,
  Settings,
  X,
  LayoutDashboard,
  ChefHat,
  Bell,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface MoreMenuProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const MORE_ITEMS = [
  { label: 'Produits', icon: Box, path: '/products' },
  { label: 'Recettes', icon: ChefHat, path: '/recipes' },
  { label: 'Alertes', icon: Bell, path: '/alerts' },
  { label: 'Historique', icon: History, path: '/history' },
  { label: 'Fournisseurs', icon: Truck, path: '/suppliers' },
  { label: 'Lots / DLC', icon: Calendar, path: '/lots' },
  { label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
  { label: 'Paramètres', icon: Settings, path: '/settings' },
];

export function MoreMenu({ open, onOpenChange }: MoreMenuProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const handleItemClick = (path: string) => {
    navigate(path);
    onOpenChange(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/50 z-50 md:hidden"
            onClick={() => onOpenChange(false)}
            aria-hidden="true"
          />

          {/* Sheet */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-900 rounded-t-2xl z-50 md:hidden safe-area-bottom"
            role="dialog"
            aria-modal="true"
            aria-label="Menu de navigation"
          >
            {/* Handle */}
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-12 h-1 bg-gray-300 dark:bg-gray-700 rounded-full" />
            </div>

            {/* Close button */}
            <button
              onClick={() => onOpenChange(false)}
              className="absolute top-3 right-3 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors touch-target"
              aria-label="Fermer le menu"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>

            {/* Title */}
            <h2 className="text-lg font-semibold text-center text-gray-900 dark:text-white mb-4">
              Navigation
            </h2>

            {/* Navigation Grid */}
            <nav className="grid grid-cols-3 gap-3 p-4 pb-8">
              {MORE_ITEMS.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;

                return (
                  <button
                    key={item.path}
                    onClick={() => handleItemClick(item.path)}
                    className={cn(
                      'flex flex-col items-center gap-2 p-4 rounded-xl transition-colors min-h-[80px] touch-feedback',
                      isActive
                        ? 'bg-primary/10 text-primary'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400'
                    )}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    <Icon className="w-6 h-6" aria-hidden="true" />
                    <span className="text-sm font-medium">{item.label}</span>
                  </button>
                );
              })}
            </nav>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
